import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  decodeUserFromToken,
  fetchProfileRemote,
  getContributorAccessToken,
} from "@/lib/authApi";
import { fetchMyContributionsRemote } from "@/lib/termsApi";
import { supabase, asLexiconSelectChain } from "@/integrations/supabase/client";
import type { Term } from "@/admin/types";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Clock, CheckCircle, XCircle, FileText, LogIn } from "lucide-react";
import { toastApiError } from "@/lib/toastApiError";
import { richTextToPlainText } from "@/lib/richText";
import { useLanguage } from "@/contexts/LanguageContext";

type StatusKey = "pending" | "approved" | "rejected";

const statusIcons: Record<
  StatusKey,
  { icon: typeof Clock; color: string }
> = {
  pending: {
    icon: Clock,
    color: "bg-yellow-100 text-yellow-800",
  },
  approved: {
    icon: CheckCircle,
    color: "bg-green-100 text-green-800",
  },
  rejected: {
    icon: XCircle,
    color: "bg-red-100 text-red-800",
  },
};

export type ContributorContributionsSectionProps = {
  /** Increment after a successful submit to refetch rows. */
  refreshKey?: number;
  /** Opens the contribute dialog — wire from parent if you expose it. */
  onOpenSubmitForm?: () => void;
};

export function ContributorContributionsSection({
  refreshKey = 0,
  onOpenSubmitForm,
}: ContributorContributionsSectionProps) {
  const { t, language } = useLanguage();
  const dateLocale =
    language === "hi" ? "hi-IN" : language === "gu" ? "gu-IN" : "en-IN";

  const statusLabel = (key: StatusKey) =>
    key === "pending"
      ? t("contrib.statusPending")
      : key === "approved"
        ? t("contrib.statusApproved")
        : t("contrib.statusRejected");

  const {
    contributorUser,
    isContributorAuthenticated,
    isLoading: authLoading,
  } = useAuth();
  const [terms, setTerms] = useState<Term[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!contributorUser) {
      setTerms([]);
      setIsLoading(false);
      return;
    }
    const fetchMyTerms = async () => {
      setIsLoading(true);
      const token = getContributorAccessToken();
      const fromTok = token ? decodeUserFromToken(token) : null;
      const userEmail =
        contributorUser.email ?? fromTok?.email ?? null;
      const emailTrim = userEmail?.trim() ?? "";

      if (token) {
        const { profile } = await fetchProfileRemote(
          contributorUser.id,
          "portal"
        );
        const fromProfile = profile?.email?.trim() || null;
        const fromSession = contributorUser.email?.trim() || null;
        const fromJwt = fromTok?.email?.trim() || null;
        const primaryEmail =
          fromProfile ?? fromSession ?? fromJwt ?? null;
        const allEmails = [fromProfile, fromSession, fromJwt].filter(
          (e): e is string => Boolean(e?.length)
        );

        const { terms: rows, error } = await fetchMyContributionsRemote({
          userId: contributorUser.id,
          userEmail: primaryEmail,
          matchEmails: allEmails,
        });
        if (error) {
          toastApiError(error);
          setTerms([]);
        } else {
          setTerms(rows);
        }
        setIsLoading(false);
        return;
      }

      if (!contributorUser.id && !emailTrim) {
        setTerms([]);
        setIsLoading(false);
        return;
      }

      const selectCols =
        "id, word, meaning_english, status, created_at, user_id, contributor_email";
      type LegacyRow = {
        id: string;
        word: string;
        meaning_english: string | null;
        status: Term["status"];
        created_at: string;
        user_id?: string | null;
        contributor_email?: string | null;
      };

      const merged = new Map<string, LegacyRow>();
      const ingest = (rows: LegacyRow[] | null) => {
        for (const r of rows ?? []) {
          merged.set(String(r.id), r);
        }
      };

      if (contributorUser.id) {
        const chainUser = asLexiconSelectChain(
          supabase.from("terms").select(selectCols)
        )
          .eq("user_id", contributorUser.id)
          .order("created_at", { ascending: false });
        const { data, error: e1 } =
          await Promise.resolve(
            chainUser as unknown as Promise<{ data: unknown; error: unknown }>
          );
        if (e1) {
          toastApiError(t("contrib.loadFailed"));
          setTerms([]);
          setIsLoading(false);
          return;
        }
        ingest(data as LegacyRow[]);
      }

      if (emailTrim) {
        const chainEmail = asLexiconSelectChain(
          supabase.from("terms").select(selectCols)
        )
          .ilike("contributor_email", emailTrim)
          .order("created_at", { ascending: false });
        const { data, error: e2 } =
          await Promise.resolve(
            chainEmail as unknown as Promise<{ data: unknown; error: unknown }>
          );
        if (e2) {
          toastApiError(t("contrib.loadFailed"));
          setTerms([]);
          setIsLoading(false);
          return;
        }
        ingest(data as LegacyRow[]);
      }

      const list = Array.from(merged.values()).sort((a, b) => {
        const ta = Date.parse(a.created_at) || 0;
        const tb = Date.parse(b.created_at) || 0;
        return tb - ta;
      });

      const nowIso = new Date().toISOString();

      setTerms(
        list.map(row => ({
          id: String(row.id),
          word: row.word ?? "",
          meaning_english: row.meaning_english ?? null,
          meaning_hindi: null,
          meaning_gujarati: null,
          pronunciation: null,
          example_usage: null,
          status: row.status as Term["status"],
          contributor_email:
            typeof row.contributor_email === "string"
              ? row.contributor_email
              : null,
          contributor_name: null,
          admin_notes: null,
          user_id: row.user_id ?? contributorUser.id,
          featured: false,
          is_enabled: true,
          audio_url: null,
          created_at: row.created_at ?? nowIso,
          updated_at: row.created_at ?? nowIso,
        }))
      );
      setIsLoading(false);
    };
    fetchMyTerms();
  }, [contributorUser, refreshKey]);

  const counts = {
    total: terms.length,
    pending: terms.filter(t => t.status === "pending").length,
    approved: terms.filter(t => t.status === "approved").length,
    rejected: terms.filter(t => t.status === "rejected").length,
  };

  if (authLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="border-primary h-8 w-8 animate-spin rounded-full border-b-2" />
      </div>
    );
  }

  if (!isContributorAuthenticated || !contributorUser) {
    return (
      <Card className="border-border/50 mx-auto max-w-lg">
        <CardContent className="flex flex-col items-center gap-4 py-12 text-center">
          <LogIn className="text-muted-foreground h-12 w-12" />
          <div>
            <h2 className="text-foreground font-serif text-xl font-semibold">
              {t("contrib.signInTitle")}
            </h2>
            <p className="text-muted-foreground mt-2 text-sm">
              {t("contrib.signInBody")}
            </p>
          </div>
          <Button asChild className="bg-primary text-primary-foreground">
            <Link to="/login">{t("contrib.signInButton")}</Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="mx-auto grid max-w-6xl grid-cols-2 gap-4 md:grid-cols-4">
        {[
          {
            label: t("contrib.statTotal"),
            value: counts.total,
            color: "bg-primary/10 text-primary",
          },
          {
            label: t("contrib.statPending"),
            value: counts.pending,
            color: "bg-yellow-100 text-yellow-800",
          },
          {
            label: t("contrib.statApproved"),
            value: counts.approved,
            color: "bg-green-100 text-green-800",
          },
          {
            label: t("contrib.statRejected"),
            value: counts.rejected,
            color: "bg-red-100 text-red-800",
          },
        ].map(s => (
          <Card key={s.label} className="border-border/50">
            <CardContent className="p-4 text-center">
              <p className="text-foreground text-2xl font-bold">{s.value}</p>
              <Badge variant="secondary" className={s.color}>
                {s.label}
              </Badge>
            </CardContent>
          </Card>
        ))}
      </div>

      
      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="border-primary h-8 w-8 animate-spin rounded-full border-b-2" />
        </div>
      ) : terms.length === 0 ? (
        <Card className="border-border/50 mx-auto max-w-xl">
          <CardContent className="py-12 text-center">
            <FileText className="text-muted-foreground mx-auto mb-4 h-12 w-12" />
            <h3 className="text-foreground mb-2 font-serif text-xl font-semibold">
              {t("contrib.noContributions")}
            </h3>
            <p className="text-muted-foreground mx-auto mb-6 max-w-sm text-sm">
              {t("contrib.noContributionsHint")}
            </p>
            {onOpenSubmitForm ? (
              <Button
                type="button"
                onClick={onOpenSubmitForm}
                className="bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                {t("contribute.submitNew")}
              </Button>
            ) : null}
          </CardContent>
        </Card>
      ) : (
        <Card className="border-border/50 mx-auto w-full max-w-6xl overflow-hidden">
          <CardContent className="overflow-x-auto p-0">
            <Table className="w-full min-w-[640px]">
              <TableHeader className="bg-[#193876] [&_tr]:border-white/15">
                <TableRow className="border-transparent hover:bg-transparent">
                  <TableHead className="w-10 px-3 text-center font-semibold tracking-wide text-white xl:w-12">
                    {t("contrib.colIndex")}
                  </TableHead>
                  <TableHead className="w-[14%] min-w-[5rem] px-3 font-semibold tracking-wide text-white">
                    {t("contrib.colWord")}
                  </TableHead>
                  <TableHead className="w-[22%] px-3 font-semibold tracking-wide text-white">
                    {t("contrib.colMeaningEn")}
                  </TableHead>
                  <TableHead className="w-[13%] px-3 font-semibold tracking-wide text-white">
                    {t("contrib.colStatus")}
                  </TableHead>
                  <TableHead className="w-[12%] px-3 font-semibold tracking-wide text-white">
                    {t("contrib.colSubmitted")}
                  </TableHead>
                  <TableHead className="hidden w-[20%] px-3 font-semibold tracking-wide text-white lg:table-cell">
                    {t("contrib.colEmail")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {terms.map((term, index) => {
                  const st: StatusKey =
                    term.status === "approved" || term.status === "rejected"
                      ? term.status
                      : "pending";
                  const cfg = statusIcons[st];
                  const Icon = cfg.icon;
                  return (
                    <TableRow key={term.id}>
                      <TableCell className="text-muted-foreground px-3 text-center align-middle tabular-nums">
                        {index + 1}
                      </TableCell>
                      <TableCell className="px-3 align-middle font-semibold">
                        <span className="line-clamp-2 break-words">
                          {term.word}
                        </span>
                      </TableCell>
                      <TableCell className="text-muted-foreground px-3 align-middle">
                        <span className="line-clamp-2 break-words">
                          {richTextToPlainText(term.meaning_english) || "—"}
                        </span>
                      </TableCell>
                      <TableCell className="px-3 align-middle">
                        <Badge
                          variant="secondary"
                          className={`${cfg.color} whitespace-nowrap text-xs`}
                        >
                          <Icon className="mr-1 h-3 w-3" />
                          {statusLabel(st)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground px-3 align-middle whitespace-nowrap text-xs">
                        {new Date(term.created_at).toLocaleDateString(
                          dateLocale,
                          {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          }
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground hidden px-3 align-middle text-xs lg:table-cell lg:max-w-[12rem]">
                        <span className="line-clamp-2 break-all">
                          {term.contributor_email?.trim()
                            ? term.contributor_email
                            : "—"}
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
